import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import cx from 'classnames';
import styles from './DownloadModal.module.scss';
import ModalHeader from '../../Modal/ModalHeader';
import ProgressBar from '../../ProgressBar/ProgressBar';
import { CustomButton } from '../../Button/Button';
import { Icon } from '../../Icon';
import VideoService from '../../../service/VideoService';
import CmsAPI from '../../../../common/apis/CmsAPI';
import { useTranslation } from 'react-i18next';
import Images from '../../../constants/Images';
import { PreferenceSwitch } from '../../../routes/newVideo/VideoSidebarMenu/PreferenceSwiches/PreferenceSwich';
import Spinner from '../../../../common/components/Spinner';
import JSZip from 'jszip';
import supportedLanguages from '../../../../common/constants/supportedLanguages.json';
import LanguageSelect from '../../../features/SubtitlesEditor/components/LanguageSelect/LanguageSelect';
import Tooltip from '../../Tooltip/Tooltip';

import { uploadScenesTextPanelsPng } from '../../../utils/SimulatedItemsUtility';
import { ToastNotification } from '../../Toast/Toast';
import { parseString } from '../../../utils/stringHelper';

import useDownloadVideo from '../../../hooks/useDownloadVideo';
import { useUserContext } from '../../../context/UserContext';

const noSubtitle = {
  id: null,
  name: 'download_modal.no_subtitles',
  src: '',
};

function DownloadModalContent({ onClose, video }) {
  const { t } = useTranslation();
  const defaultDescription = t('download_modal.init_message');

  const [loadError, setLoadError] = useState(false);
  const [title, setTitle] = useState(video.name);
  const [description, setDescription] = useState(defaultDescription);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [socialsFriendly, setSocialsFriendly] = useState(false);
  const [download, setDownload] = useState(0);
  const [subtitlesLanguage, setSubtitlesLanguage] = useState(noSubtitle);
  const [burnCta, setBurnCta] = useState(true);
  const [regenerateBtnHover, setRegenerateBtnHover] = useState(false);
  const [regenerateInProgress, setRegenerateInProgress] = useState(false);

  const { isSuperAdmin } = useUserContext();


  const { onStart, handleDownloadMetadata, percentage, status, downloadLink } = useDownloadVideo({
    video,
    subtitlesLanguage,
    burnCta,
    socialsFriendly,
    callback: onClose,
  });

  const isDownloadResourcesAllowed = useMemo(() => {
    if (isSuperAdmin && status == 'pending') {
      return video.appConfig.features?.find(feature => feature.name === 'vormats_pro')?.enabled;
    }
    return false;
  }, [video, isSuperAdmin, status])


  const hasTextPanels = useMemo(() => {
    return video.scenes.some(item => item.hasOwnProperty('nameCard'));
  }, [video]);

  const availableLanguages = useMemo(() => {
    const originalLanguageId = video?.subtitlesMeta?.find(({ original }) => original)?.language;
    const originalLanguage = supportedLanguages.find(({ id }) => id === originalLanguageId);

    if (originalLanguage) {
      setSubtitlesLanguage(originalLanguage);
    }

    const languages =
      video?.subtitlesMeta
        ?.filter(({ original }) => !original)
        ?.map(({ language }) => supportedLanguages.find(({ id }) => id === language) || {})
        ?.filter(({ id }) => id) || [];

    return [noSubtitle, originalLanguage, ...languages];
  }, [supportedLanguages, video?.subtitlesMeta]);

  const capitalize = string => {
    if (string) {
      return string[0].toUpperCase() + string.slice(1);
    }
  };

  const updateDownload = ({ progress }) => {
    setDownload(progress);
  };

  const onDownload = async () => {
    try {
      const videoInfo = { url: downloadLink, name: video.name };
      await VideoService.downloadVideo([videoInfo], updateDownload);
    } catch (err) {
      // If VideoService.downloadVideo() returns an error, use a simple download link without a custom name
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = downloadLink;
      // Custom name works only with base64
      a.download = video.name + '.mp4';

      document.body.appendChild(a);
      a.click();
      a.remove();
    }

    onClose();
    CmsAPI.logEvent(video.id, { eventType: 'video_download' }).catch(err => console.log(err));
  };

  const handleSrtDownload = async e => {
    e.preventDefault();

    const zip = new JSZip();
    const folderName = `${video.id}_srt`;
    const folder = zip.folder(folderName); // folder name where all files will be placed in
    await Promise.all(
      video.subtitlesMeta
        .filter(item => item.visible)
        .map(async item => {
          const blobPromise = fetch(item.srtPath).then(res => res.blob());
          const name = `${video.id}-${item.language}.srt`;
          folder.file(name, blobPromise);
        })
    );

    await zip.generateAsync({ type: 'blob' }).then(blob => {
      var temporaryDownloadLink = document.createElement('a');
      const href = URL.createObjectURL(blob);
      temporaryDownloadLink.setAttribute('href', href);
      temporaryDownloadLink.setAttribute('target', '_blank');
      temporaryDownloadLink.setAttribute('download', folderName);
      temporaryDownloadLink.click();
      URL.revokeObjectURL(href);
    });
  };

  const handleRegenerateTextPanels = useCallback(async () => {
    setRegenerateInProgress(true);
    const failedTextPanels = await uploadScenesTextPanelsPng(
      video.id,
      video.scenes.filter(item => item.hasOwnProperty('nameCard'))
    );
    await CmsAPI.bulkUpdateScenes(
      video.scenes
        .filter(item => item.hasOwnProperty('nameCard'))
        .map(scene => ({
          id: scene.id,
          storyId: video.id,
          textPanel: {
            bottomText: scene.nameCard.bottomText,
            topText: scene.nameCard.topText,
          },
        }))
    );
    if (failedTextPanels.length) {
      ToastNotification({
        message: parseString(
          t('clip_organizer.saving.failed_to_upload_text_panels', {
            scenes: video.scenes
              .filter(item => failedTextPanels.find(id => id === item.id))
              .map(item => `'${item.name}'`)
              .join(', '),
          })
        ),
      });
    } else {
      ToastNotification({
        iconName: 'success',
        message: t('download_modal.regenerate_resources.succeeded'),
      });
    }

    setRegenerateInProgress(false);
  }, [video]);

  const source = capitalize(video.source);

  useEffect(() => {
    if (downloadLink) {
      onDownload();
    }
  }, [downloadLink]);

  useEffect(() => {
    switch (status) {
      case 'pending':
        setTitle(video.name);
        setDescription(defaultDescription);
        break;

      case 'processing':
        setTitle(t('download_modal.processing_title'));
        if (window.location.href.indexOf('engage') > -1) {
          setDescription(t('download_modal.before_processing_message'));
        } else {
          setDescription(t('download_modal.processing_video_message'));
        }
        break;

      case 'downloading':
        setTitle(t('download_modal.downloading'));
        break;

      default:
        setTitle(video.name);
        setDescription(defaultDescription);
        break;
    }
  }, [status, video]);

  useEffect(() => {
    if (status === 'processing' && percentage >= 99) {
      setTitle(t('download_modal.almost_done'));
    }
  }, [percentage]);

  const toggleAdvancedOptions = () => {
    setShowAdvancedOptions(!showAdvancedOptions);
  };

  const toggleSocialsFriendly = () => {
    setSocialsFriendly(prevState => !prevState);
  };

  const toggleBurnCta = () => {
    setBurnCta(prevState => !prevState);
  };

  const advancedOptions = useMemo(
    () => [
      {
        visible: video.orientation === 'portrait' && video?.subtitlesMeta.length !== 0,
        disabled: !subtitlesLanguage?.id,
        key: 'socialsFriendly',
        text: t('download_modal.socials_friendly'),
        isOn: socialsFriendly,
        action: toggleSocialsFriendly,
      },
      {
        visible: true,
        key: 'burnCta',
        text: t('download_modal.burn_cta'),
        isOn: burnCta,
        action: toggleBurnCta,
      },
    ],
    [video, subtitlesLanguage, socialsFriendly, toggleSocialsFriendly, toggleBurnCta]
  );

  useEffect(() => {
    if (advancedOptions.every(item => item.visible) && !subtitlesLanguage?.id) {
      setSocialsFriendly(false);
    }
  }, [subtitlesLanguage]);

  return (
    <>
      <ModalHeader className={cx(styles.header)}>
        <h1 className={styles.headerTitle}>{t('general.video_action_button.download')}</h1>
        <div className={styles.closeBtn} onClick={onClose}>
          <Icon className={styles.icon} src="close" />
        </div>
      </ModalHeader>

      <div className={styles.content}>
        <div className={styles.caption}>{t('download_modal.as_mp4')}</div>

        {video.thumbnail ? (
          loadError ? (
            <Icon className={styles.thumbnail} src="mediaMissing" />
          ) : (
            <img className={cx(styles.thumbnail)} src={video.thumbnail} alt={t('my_video.tools.thumbnail')} onError={() => setLoadError(true)} />
          )
        ) : (
          <Spinner width="30px" height="30px" size="3px" />
        )}

        <div className={cx(styles.titleContainer, { [styles.noPostInfo]: status !== 'pending' })}>
          <h1 className={styles.title}>{title}</h1>
          {percentage >= 99 && status === 'processing' && <Spinner width="30px" height="30px" size="3px" className={styles.spinner} />}
        </div>

        {status === 'pending' && (
          <div className={styles.postInfo}>
            <span>{t('general.date', { date: new Date(`${video.updateDate}`) })}</span> | <span>{source}</span>
          </div>
        )}

        {status === 'pending' && advancedOptions.some(item => item.visible) && (
          <div className={styles.actionsContainer}>
            {video?.subtitlesMeta.length > 0 && (
              <div className={styles.subtitleLanguageContainer}>
                <span className={styles.subtitleLanguageTitle}>{t('download_modal.select_language')}</span>
                <LanguageSelect
                  options={availableLanguages}
                  onSelect={setSubtitlesLanguage}
                  currentlySelected={subtitlesLanguage}
                  className={styles.languagesSelect}
                  listClassName={styles.languagesSelectList}
                  listItemClassName={styles.languagesListItem}
                  selectedItemClassName={styles.languagesSelectedItem}
                />
              </div>
            )}

            {advancedOptions.some(item => item.visible) && (
              <div
                className={cx(styles.optionsWrapper, {
                  [styles.showOptions]: showAdvancedOptions,
                  [styles.showBothOptions]: showAdvancedOptions && advancedOptions.filter(item => item.visible).length === 2,
                  [styles.hasTextPanels]: hasTextPanels,
                })}
              >
                <div className={cx(styles.advancedOptionsLink)} onClick={() => toggleAdvancedOptions()}>
                  {t('download_modal.advanced_options')}
                  <img
                    src={Images.ArrowDownIcon}
                    alt=""
                    className={cx(styles.arrowButton, { [styles.turnArrow]: showAdvancedOptions })}
                  />
                </div>
                <div className={cx(styles.advancedOptionsContainer, { [styles.showOptions]: showAdvancedOptions })}>
                  {advancedOptions.map(item =>
                    item.visible ? <PreferenceSwitch item={item} key={item.key} className={styles.switch} /> : null
                  )}
                  {hasTextPanels && (
                    <div className={styles.regenerateBtnContainer}>
                      <CustomButton
                        label={t('download_modal.regenerate_resources')}
                        variant="secondary"
                        loading={regenerateInProgress}
                        loadingSpinner={true}
                        onMouseOver={() => setRegenerateBtnHover(true)}
                        onMouseLeave={() => setRegenerateBtnHover(false)}
                        onClick={handleRegenerateTextPanels}
                      />
                      <Tooltip text={t('download_modal.regenerate_resources.info')} isHover={regenerateBtnHover} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {status === 'processing' && percentage < 99 && (
          <ProgressBar percentage={percentage} readyMsg={t('download_modal.ready_for_download')} />
        )}

        {status === 'downloading' && <ProgressBar percentage={download} readyMsg={t('download_modal.ready_for_download')} />}

        {status !== 'downloading' && (
          <div className={cx(styles.caption, { [styles.showDivider]: status === 'pending' && percentage === 0 })}>{description}</div>
        )}

        {status !== 'processing' && (
          <div className={cx(styles.footer, styles.twoButtons)}>
            <div className={styles.buttonsContainer}>
              {video.subtitlesMeta.length ? (
                <CustomButton
                  label={t('download_modal.download_srt_button')}
                  variant="link"
                  onClick={handleSrtDownload}
                  className={styles.footerDownloadButton}
                />
              ) : null}

              {isDownloadResourcesAllowed && (
                <CustomButton
                  label={t('download_modal.download_metadata_button')}
                  variant="link"
                  onClick={handleDownloadMetadata}
                  className={styles.footerDownloadButton}
                />
              )}
            </div>

            {status == 'pending' && <CustomButton label={t('download_modal.start')} onClick={() => onStart()} variant="primary" />}

            {status === 'error' && <p>{t('download_modal.fail')}</p>}
          </div>
        )}
      </div>
    </>
  );
}

export default DownloadModalContent;
